import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";

const ACCENT = "#4FA3D9";

const TM = () => <sup className="ml-0.5 text-[0.5em] align-super text-white/45">™</sup>;

const Sec = ({ n, title, children }) => (
  <section className="mt-11">
    <h2 className="font-display text-xl font-medium tracking-tight text-white sm:text-2xl">
      {n}. {title}
    </h2>
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const P = ({ children }) => <p className="text-base leading-relaxed text-white/65">{children}</p>;

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-base leading-relaxed text-white/65">
        <span className="mt-[9px] h-1 w-1 flex-shrink-0 rounded-full" style={{ background: ACCENT }} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Mail = () => (
  <a
    href="mailto:hello@adaptience.com"
    className="font-medium underline decoration-white/25 underline-offset-2 transition-colors hover:decoration-white/60"
    style={{ color: ACCENT }}
  >
    hello@adaptience.com
  </a>
);

export const TermsOfUse = ({ onBack }) => {
  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="terms-page">
      <Seo
        title="Terms of Use | Adaptience"
        description="The Terms of Use governing access to the Adaptience interactive life simulation platform."
        canonical="/terms"
      />
      {/* subtle top glow */}
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[60vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>

      {/* NAV */}
      <Navbar />

      <main className="relative z-10 mx-auto w-full max-w-3xl px-6 py-16 sm:px-10 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>
            Adaptience<TM />
          </p>
          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            Terms of Use
          </h1>

          {/* ===================================================================
              TERMS OF USE CONTENT
          =================================================================== */}
          <div className="mt-10" data-testid="terms-content">
            <p className="text-sm text-white/40">Last updated: August 2026</p>
            <div className="mt-6 space-y-4">
              <P>These Terms of Use govern access to and use of the Adaptience™ website, simulations and related experiences.</P>
              <P>By accessing or using Adaptience™, you agree to these Terms.</P>
              <P>Where the participant is a child, a parent or legal guardian should review these Terms and supervise or authorise the child's use as appropriate.</P>
            </div>

            <Sec n={1} title="About Adaptience™">
              <P>Adaptience™ is designed to help people practise navigating difficult and uncertain real-life situations through simulated experiences.</P>
              <P>We are currently beginning with simulations designed for children aged 10–14.</P>
              <P>Adaptience™ places participants in simulated situations, allows them to make decisions and respond, and provides opportunities to reflect and practise.</P>
            </Sec>

            <Sec n={2} title="Educational and Practice Purpose">
              <P>Adaptience™ is provided for educational, learning and practice purposes.</P>
              <P>It is not a substitute for professional medical, psychological, psychiatric, counselling, therapeutic, safeguarding, legal or other professional services.</P>
              <P>Adaptience™ does not diagnose mental-health conditions or determine a participant's psychological state.</P>
            </Sec>

            <Sec n={3} title="Parent Adaptience Practice Profile">
              <P>Some simulations may generate a Parent Adaptience Practice Profile or similar post-simulation observations.</P>
              <P>These observations are based on responses made during a simulated experience.</P>
              <P>They should not be interpreted as:</P>
              <Bullets items={[
                "a psychological assessment;",
                "a clinical assessment;",
                "a diagnosis;",
                "a personality assessment;",
                "a prediction of future behaviour;",
                "a measure of intelligence or ability; or",
                "a professional recommendation about a child.",
              ]} />
              <P>A child's response in a simulation may differ substantially from how the child would respond in a real situation.</P>
              <P>The Practice Profile is intended to support reflection, conversation and further practice.</P>
            </Sec>

            <Sec n={4} title="Children and Parent/Guardian Involvement">
              <P>Our current children's simulations are intended to be used with the involvement and permission of a parent or legal guardian.</P>
              <P>Parents and guardians are responsible for deciding whether a simulation is suitable for their child.</P>
              <P>Children should not provide unnecessary personal identifying information when responding during a simulation.</P>
            </Sec>

            <Sec n={5} title="Simulated Situations">
              <P>Adaptience™ simulations may include situations involving rejection, exclusion, peer pressure, friendship difficulties, failure, embarrassment, unexpected change and other challenging life experiences.</P>
              <P>These situations are fictional simulations designed for practice.</P>
              <P>They are not representations of actual events involving the participant.</P>
              <P>Parents and guardians should consider their child's individual circumstances before allowing participation.</P>
            </Sec>

            <Sec n={6} title="Artificial Intelligence">
              <P>Adaptience™ may use artificial intelligence to interpret responses, generate character reactions, adapt simulations and produce post-simulation observations.</P>
              <P>AI-generated outputs are not guaranteed to be complete, accurate or appropriate in every circumstance.</P>
              <P>Users should exercise judgment when considering AI-generated information.</P>
            </Sec>

            <Sec n={7} title="Real-Life Difficulties">
              <P>Adaptience™ is designed for preparation and practice.</P>
              <P>It should not be relied upon as the sole source of support when a child or other participant is experiencing a serious real-life problem.</P>
              <P>Where there is a genuine concern about a child's safety, wellbeing, abuse, bullying, self-harm, violence or another serious situation, a parent or guardian should seek appropriate assistance from qualified people or relevant services.</P>
            </Sec>

            <Sec n={8} title="Early-Stage Product">
              <P>Adaptience™ is currently an early-stage platform.</P>
              <P>Features, simulations, content, availability, AI behaviour and functionality may change as the product develops.</P>
              <P>We may add, modify, suspend or discontinue features or simulations without prior notice.</P>
            </Sec>

            <Sec n={9} title="Acceptable Use">
              <P>Users must not misuse Adaptience™, attempt to interfere with its operation, access systems without authorisation, submit unlawful or harmful content, or use the service in a way that violates applicable law or the rights of others.</P>
            </Sec>

            <Sec n={10} title="Intellectual Property">
              <P>The Adaptience™ name, branding, simulations, written content, methodology, visual design and other original materials are owned by or licensed to the operator of Adaptience™, except where otherwise stated.</P>
              <P>Access to Adaptience™ does not grant users ownership of its intellectual property.</P>
              <P>Content may not be commercially reproduced, redistributed or used to create competing materials without appropriate permission.</P>
            </Sec>

            <Sec n={11} title="Availability">
              <P>We aim to provide a reliable service, but Adaptience™ may occasionally be unavailable because of maintenance, technical problems, third-party services or other circumstances.</P>
              <P>We do not guarantee uninterrupted or error-free availability.</P>
            </Sec>

            <Sec n={12} title="Limitation of Responsibility">
              <P>Adaptience™ is a simulation and practice tool.</P>
              <P>To the extent permitted by applicable law, Adaptience™ and its operator are not responsible for decisions made solely in reliance on simulation outputs, AI-generated responses or Practice Profile observations.</P>
              <P>Nothing in these Terms excludes rights or responsibilities that cannot lawfully be excluded.</P>
            </Sec>

            <Sec n={13} title="Privacy">
              <P>Use of Adaptience™ is also subject to our Privacy Policy, which explains how information is handled.</P>
            </Sec>

            <Sec n={14} title="Changes to These Terms">
              <P>We may update these Terms as Adaptience™ develops.</P>
              <P>The latest version will be published on the website with the date of the most recent update.</P>
              <P>Continued use of the service following an update constitutes acceptance of the revised Terms where permitted by applicable law.</P>
            </Sec>

            <Sec n={15} title="Contact">
              <P>Questions about these Terms may be sent to:</P>
              <P>Adaptience™</P>
              <P>Email: <Mail /></P>
            </Sec>
          </div>
          {/* ===================== END TERMS OF USE CONTENT ===================== */}

          <div className="mt-16 border-t border-white/[0.08] pt-10">
            <button
              onClick={onBack}
              data-testid="terms-back-bottom-btn"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
              style={{ background: ACCENT }}
            >
              <ArrowLeft size={15} />
              Back to Adaptience<TM />
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsOfUse;
